import { useEffect, useId, useState } from 'react'
import { getBookingDraft, saveBookingDraft } from '../../data/ceramic-booking'
import { callSupabaseFunction } from '../../lib/api'
import {
  formatSlotTimeLabel,
  hmInTimeZone,
  ymdInTimeZone,
  type AvailableDate,
  type GeneratedSlot,
} from '../../lib/appointment-scheduling'
import { appointmentReservationSchema, zodFieldErrors } from '../../lib/booking-validation'
import { Button } from '../ui/button'

export interface AppointmentBookingView {
  publicReference: string
  paymentStatus?: string
  appointmentStatus?: string
  appointmentStartAt?: string | null
  appointmentTimezone?: string
  appointmentHoldExpiresAt?: string | null
  reservationExpired?: boolean
  reservationActive?: boolean
  packageDisplayName: string
  productName: string
  addOnNames: string[]
  noAdditionalProtection?: boolean
  depositAmountCents?: number
  customer?: { name?: string; email?: string; phone?: string }
  vehicle?: { make?: string; model?: string }
  confirmation?: {
    publicReference: string
    protection: string
    productName: string
    addOnsLabel: string
    depositPaidLabel: string
    appointmentDate: string
    appointmentTime: string
    location: string
    remainingBalanceNote: string
  } | null
}

export interface BookAppointmentStepProps {
  publicReference?: string | null
  packageKey: string | null
  addOnIds: string[]
  expiredNotice?: string | null
  onReserved: (booking: AppointmentBookingView) => void
  onConfirmed: (booking: AppointmentBookingView, emailStatus?: string | null) => void
}

export function BookAppointmentStep({
  publicReference,
  packageKey,
  addOnIds,
  expiredNotice,
  onReserved,
  onConfirmed,
}: Readonly<BookAppointmentStepProps>) {
  const nameId = useId()
  const emailId = useId()
  const phoneId = useId()
  const makeId = useId()
  const modelId = useId()
  const draft = getBookingDraft()
  const [name, setName] = useState(draft.customer.name)
  const [email, setEmail] = useState(draft.customer.email)
  const [phone, setPhone] = useState(draft.customer.phone)
  const [make, setMake] = useState(draft.vehicle.make)
  const [model, setModel] = useState(draft.vehicle.model)
  const [dates, setDates] = useState<AvailableDate[]>([])
  const [slots, setSlots] = useState<GeneratedSlot[]>([])
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedTime, setSelectedTime] = useState<string | null>(null)
  const [timezone, setTimezone] = useState('Australia/Melbourne')
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    let active = true

    async function load(date?: string | null) {
      setLoading(true)
      try {
        const query: Record<string, string> = {}
        if (publicReference) query.reference = publicReference
        if (date) query.date = date
        const response = await callSupabaseFunction('booking-availability', {}, query)
        const payload = await response.json() as {
          ok?: boolean
          reason?: string
          error?: string
          nextStep?: string
          timezone?: string
          dates?: AvailableDate[]
          slots?: GeneratedSlot[]
          booking?: AppointmentBookingView
        }
        if (!active) return
        if (!payload.ok) {
          setMessage(payload.error ?? 'Unable to load appointment times.')
          return
        }
        setTimezone(payload.timezone ?? 'Australia/Melbourne')
        setDates(payload.dates ?? [])
        if (date) setSlots(payload.slots ?? [])
        const booking = payload.booking
        if (booking?.customer?.name) setName(booking.customer.name)
        if (booking?.customer?.email) setEmail(booking.customer.email)
        if (booking?.customer?.phone) setPhone(booking.customer.phone)
        if (booking?.vehicle?.make) setMake(booking.vehicle.make)
        if (booking?.vehicle?.model) setModel(booking.vehicle.model)
        if (!date && booking?.appointmentStartAt) {
          const start = new Date(booking.appointmentStartAt)
          const existingDate = ymdInTimeZone(start, booking.appointmentTimezone || payload.timezone || 'Australia/Melbourne')
          const existingTime = hmInTimeZone(start, booking.appointmentTimezone || payload.timezone || 'Australia/Melbourne')
          setSelectedDate(existingDate)
          setSelectedTime(existingTime)
        }
      } catch {
        if (active) setMessage('Unable to load appointment times.')
      } finally {
        if (active) setLoading(false)
      }
    }

    void load()
    return () => {
      active = false
    }
  }, [publicReference])

  useEffect(() => {
    if (!selectedDate) return
    let active = true
    setBusy(true)
    callSupabaseFunction('booking-availability', {}, {
      ...(publicReference ? { reference: publicReference } : {}),
      date: selectedDate,
    })
      .then((response) => response.json())
      .then((payload: { ok?: boolean; slots?: GeneratedSlot[]; reason?: string }) => {
        if (!active) return
        setSlots(payload.slots ?? [])
      })
      .catch(() => {
        if (active) setMessage('Unable to load appointment times.')
      })
      .finally(() => {
        if (active) setBusy(false)
      })
    return () => {
      active = false
    }
  }, [publicReference, selectedDate])

  function persistDetails() {
    saveBookingDraft({
      ...getBookingDraft(),
      customer: { ...getBookingDraft().customer, name, email, phone },
      vehicle: { ...getBookingDraft().vehicle, make, model },
      preferredDate: selectedDate ?? undefined,
      preferredTime: selectedTime ?? undefined,
    })
  }

  async function confirmAppointment() {
    setMessage(null)
    setFieldErrors({})
    const parsed = appointmentReservationSchema.safeParse({
      publicReference: publicReference || undefined,
      packageKey: packageKey || undefined,
      addOns: addOnIds,
      date: selectedDate,
      time: selectedTime,
      customer: { name, email, phone },
      vehicle: { make, model },
    })
    if (!parsed.success) {
      setFieldErrors(zodFieldErrors(parsed.error))
      setMessage('Please complete your details and choose an available appointment.')
      return
    }

    persistDetails()
    setBusy(true)
    try {
      const response = await callSupabaseFunction('booking-appointment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed.data),
      })
      const payload = await response.json() as {
        ok?: boolean
        error?: string
        reason?: string
        nextStep?: string
        confirmationEmailStatus?: string | null
        booking?: AppointmentBookingView
      }
      if (!payload.ok || !payload.booking) {
        setMessage(payload.error ?? 'That appointment has just become unavailable. Please choose another time.')
        if (selectedDate) {
          const refresh = await callSupabaseFunction('booking-availability', {}, {
            ...(publicReference ? { reference: publicReference } : {}),
            date: selectedDate,
          })
          const refreshed = await refresh.json() as { dates?: AvailableDate[]; slots?: GeneratedSlot[] }
          setDates(refreshed.dates ?? dates)
          setSlots(refreshed.slots ?? [])
        }
        setSelectedTime(null)
        return
      }
      saveBookingDraft({
        ...getBookingDraft(),
        publicReference: payload.booking.publicReference,
        customer: { ...getBookingDraft().customer, name, email, phone },
        vehicle: { ...getBookingDraft().vehicle, make, model },
        preferredDate: selectedDate ?? undefined,
        preferredTime: selectedTime ?? undefined,
      })
      if (payload.booking.appointmentStatus === 'confirmed' || payload.nextStep === 'confirmation') {
        onConfirmed(payload.booking, payload.confirmationEmailStatus)
        return
      }
      onReserved(payload.booking)
    } catch {
      setMessage('Unable to confirm that appointment. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  function errorFor(id: string) {
    const text = fieldErrors[id]
    if (!text) return null
    return <span className="field-error">{text}</span>
  }

  return (
    <section aria-labelledby="book-appointment-heading" className="book-step-panel">
      <h2 id="book-appointment-heading">Choose Your Appointment</h2>
      <p>Choose an available appointment, then add your contact and vehicle details to continue to the $100 deposit.</p>
      <p className="timezone-note">Times are shown in Australia/Melbourne.</p>
      {expiredNotice ? <p className="inspection-notice" role="alert">{expiredNotice}</p> : null}
      {message ? <p className="inspection-notice" role="alert">{message}</p> : null}

      <h3>Date</h3>
      {loading && dates.length === 0 ? <p>Loading available dates…</p> : null}
      {!loading && dates.length === 0 ? (
        <p className="inspection-notice" role="status">
          No appointment times are currently available. Please try again shortly.
        </p>
      ) : (
        <div className="appointment-date-grid" role="group" aria-label="Available appointment dates">
          {dates.map((item) => {
            const selected = item.date === selectedDate
            return (
              <button
                aria-pressed={selected}
                className={selected ? 'selected' : ''}
                key={item.date}
                onClick={() => {
                  setSelectedDate(item.date)
                  setSelectedTime(null)
                  persistDetails()
                }}
                type="button"
              >
                {selected ? <span className="slot-selected-label">Selected</span> : null}
                {item.label.replace(/^\w+, /, '')}
              </button>
            )
          })}
        </div>
      )}

      <h3>Time</h3>
      {!selectedDate ? (
        <p>Select a date to see available times.</p>
      ) : (
        <div className="time-grid" role="group" aria-label="Available appointment times">
          {slots.map((slot) => {
            const selected = slot.time === selectedTime
            return (
              <button
                aria-pressed={selected}
                className={selected ? 'selected' : ''}
                disabled={!slot.available || busy}
                key={slot.startAt}
                onClick={() => {
                  setSelectedTime(slot.time)
                  persistDetails()
                }}
                type="button"
              >
                {selected ? <span className="slot-selected-label">Selected</span> : null}
                {formatSlotTimeLabel(slot.time)}
              </button>
            )
          })}
        </div>
      )}
      <p className="timezone-note">Studio timezone: {timezone}.</p>

      <h3>Your details</h3>
      <div className="appointment-form vehicle-form">
        <label htmlFor={nameId}>
          Name
          <input
            autoComplete="name"
            id={nameId}
            onChange={(event) => setName(event.target.value)}
            value={name}
          />
          {errorFor('customer.name')}
        </label>
        <label htmlFor={emailId}>
          Email
          <input
            autoComplete="email"
            id={emailId}
            onChange={(event) => setEmail(event.target.value)}
            type="email"
            value={email}
          />
          {errorFor('customer.email')}
        </label>
        <label htmlFor={phoneId}>
          Phone
          <input
            autoComplete="tel"
            id={phoneId}
            onChange={(event) => setPhone(event.target.value)}
            type="tel"
            value={phone}
          />
          {errorFor('customer.phone')}
        </label>
        <label htmlFor={makeId}>
          Vehicle make
          <input
            id={makeId}
            onChange={(event) => setMake(event.target.value)}
            value={make}
          />
          {errorFor('vehicle.make')}
        </label>
        <label htmlFor={modelId}>
          Vehicle model
          <input
            id={modelId}
            onChange={(event) => setModel(event.target.value)}
            value={model}
          />
          {errorFor('vehicle.model')}
        </label>
      </div>
      <Button disabled={busy || !packageKey} onClick={() => void confirmAppointment()} type="button">
        Continue to Deposit
      </Button>
    </section>
  )
}
